// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Allow-list for Khora file-download URLs. A download URL is a presigned S3 ref the aw-server produced
// for a large offloaded export; before the client fetches it (chatbot handleDownloadFile), it must be
// https AND on an AirWorks-verified host. This is defense-in-depth against an SSRF-style fetch: the URL
// is server-signed rather than user-typed, but validating it here means a tampered or unexpected chat
// response can't turn a user download into a fetch of an arbitrary target. Presigned S3 GET URLs are
// always https on *.amazonaws.com (virtual-hosted `bucket.s3.region.amazonaws.com` or path-style
// `s3.region.amazonaws.com/bucket`); the asset CDN is *.airworks.io. The leading dot in each suffix
// check stops look-alike hosts (`amazonaws.com.evil.com`, `evil-amazonaws.com`) from slipping through.
// Pure (no Esri/store imports) so the allow-list stays unit-testable. An untrusted url falls back to the
// inline b64 at the call site, or the download is skipped.
const isTrustedDownloadUrl = (raw: string): boolean => {
  try {
    const parsed = new URL(raw);
    // Scheme gate: only https. Rejects http and the dangerous pseudo-schemes (javascript:, data:, file:).
    if (parsed.protocol !== 'https:') return false;
    // Host gate: only AirWorks-verified hosts. The leading dot means the verified name must be a real
    // domain suffix, so look-alikes (amazonaws.com.evil.com, evil-amazonaws.com) do NOT match.
    const host = parsed.hostname.toLowerCase();
    return (
      host === 'amazonaws.com'
      || host.endsWith('.amazonaws.com') // presigned S3 (virtual-hosted or path-style)
      || host === 'airworks.io'
      || host.endsWith('.airworks.io') // AirWorks asset CDN
    );
  } catch {
    // new URL() threw -> not a parseable absolute URL -> untrusted. Never let a bad string reach fetch().
    return false;
  }
};

export default isTrustedDownloadUrl;
